/* @flow */

import type { ReadOnlyKeyValuePair } from './types';

type FetchOptions = ReadOnlyKeyValuePair<string | Headers | AbortSignal>;

export enum XhrResponseType {
  ArrayBuffer = 'arraybuffer',
  Blob = 'blob',
  Json = 'json',
  Text = 'text',
  Xml = 'document',
}

const getXhrResponseTypeFromString = (responseType: string): XhrResponseType => XhrResponseType.cast(responseType) ?? XhrResponseType.Text;

const handleFetchError: (response: any) => Promise<any> = async (response) => {
  const { ok, status, statusText } = response;

  if (ok) {
    return response;
  }

  // 1. Check statusText first
  if (statusText) {
    throw new Error(`${status}:${statusText}`);
  }

  // 2. Check content-type header next
  const contentType = response.headers.get('content-type');

  if (!contentType) {
    throw new Error(status.toString());
  }

  // 3. Check content-type header value
  if (contentType.startsWith('text/plain')) {
    // Read text response
    const textError = await response.text();
    throw new Error(`${status}:${textError}`);
  }

  if (contentType.startsWith('application/json')) {
    // Read JSON response
    const { error, errorCode } = await response.json();
    throw new Error(`${status}:${error}:${errorCode ?? ''}`);
  }

  // 4. Return status code as default
  throw new Error(status.toString());
};

const fetchJsonWithErrorCheck: (url: URL | string, options?: FetchOptions) => Promise<any> = async (url, options) => {
  const response = await fetch(url, options);
  const checkedResponse = await handleFetchError(response);
  return checkedResponse.json();
};

const fetchTextWithErrorCheck: (url: URL | string, options?: FetchOptions) => Promise<any> = async (url, options) => {
  const response = await fetch(url, options);
  const checkedResponse = await handleFetchError(response);
  return checkedResponse.text();
};

const fetchStatusWithErrorCheck: (url: URL | string, options?: FetchOptions) => Promise<any> = async (url, options) => {
  const response = await fetch(url, options);
  const { status } = await handleFetchError(response);
  return status;
};

export { fetchJsonWithErrorCheck, fetchStatusWithErrorCheck, fetchTextWithErrorCheck, getXhrResponseTypeFromString };
